'use client';
import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { supabase } from '../lib/supabase';
import { User, Session } from '@supabase/supabase-js';

interface UserProfile {
  id: string
  email?: string
  full_name?: string
  role?: string
  phone?: string
  company_name?: string
  bio?: string
  profile_picture_url?: string
  address_line?: string
  city?: string
  state?: string
  postal_code?: string
  country?: string
  join_date?: string
  last_active?: string
  is_active?: boolean
  [key: string]: any
}

interface AuthContextType {
  user: User | null
  userProfile: UserProfile | null
  loading: boolean
  profileLoading: boolean
  signIn: (email: string, password: string) => Promise<any>
  signInWithGoogle: () => Promise<any>
  signOut: () => Promise<any>
  updateProfile: (updates: Partial<UserProfile>) => Promise<any>
  isAuthenticated: boolean
}

const AuthContext = createContext<AuthContextType | null>(null)

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) throw new Error('useAuth must be used within AuthProvider')
  return context
}

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null)
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null)
  const [loading, setLoading] = useState(true)
  const [profileLoading, setProfileLoading] = useState(false)

  const profileOperations = {
    async load(authUser: User) {
      if (!authUser?.id) return
      const userId = authUser.id
      setProfileLoading(true)
      try {
        const { data, error } = await supabase
          .from('users')
          .select('*')
          .eq('id', userId)
          .single()

        if (!error && data) {
          setUserProfile(data)
          // Update last_active timestamp
          await supabase
            .from('users')
            .update({ last_active: new Date().toISOString() })
            .eq('id', userId)
        } else if (error && (error as any).code === 'PGRST116') {
          // PGRST116 = ".single()" found no rows, i.e. the profile row is
          // genuinely missing (e.g. a Google/OAuth signup that never ran the
          // email signup form). Create one from the auth metadata so order
          // creation and the dashboard work. This is app-side only — no DB
          // schema or trigger changes required.
          //
          // IMPORTANT: use ignoreDuplicates so we only ever INSERT a brand new
          // row. We must never UPDATE an existing row here, otherwise an admin
          // or editor whose row exists could get silently downgraded to
          // 'client' on any future load where the select transiently fails.
          const meta: Record<string, any> = authUser.user_metadata || {}
          const derivedName =
            meta.full_name ||
            meta.name ||
            (authUser.email ? authUser.email.split('@')[0] : 'User')
          const avatarUrl = meta.avatar_url || meta.picture || null

          const { data: created } = await supabase
            .from('users')
            .upsert(
              {
                id: userId,
                email: authUser.email,
                full_name: derivedName,
                role: 'client',
                profile_picture_url: avatarUrl,
              },
              { onConflict: 'id', ignoreDuplicates: true }
            )
            .select()
            .maybeSingle()

          if (created) {
            setUserProfile(created)
          } else {
            // Row already existed (insert ignored). Re-read it so we load the
            // real stored role instead of assuming 'client'.
            const { data: existing } = await supabase
              .from('users')
              .select('*')
              .eq('id', userId)
              .maybeSingle()
            if (existing) setUserProfile(existing)
          }
        }
        // Any other error (RLS denial, network, etc.): do NOT write to the
        // users table. Leave the profile unset rather than risk overwriting a
        // real role with 'client'.
      } catch (error) {
        // Profile load error
      } finally {
        setProfileLoading(false)
      }
    },
    clear() {
      setUserProfile(null)
      setProfileLoading(false)
    },
  }

  const authStateHandlers = {
    onChange: (_event: string | null, session: Session | null) => {
      setUser(session?.user ?? null)
      setLoading(false)
      if (session?.user) profileOperations.load(session.user)
      else profileOperations.clear()
    },
  }

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      authStateHandlers.onChange(null, session)
    })

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(authStateHandlers.onChange)

    return () => subscription?.unsubscribe()
  }, [])

  const signIn = async (email: string, password: string) => {
    try {
      const { data, error } = await supabase.auth.signInWithPassword({ email, password })
      return { data, error }
    } catch {
      return { error: { message: 'Network error. Please try again.' } }
    }
  }

  const signInWithGoogle = async () => {
    try {
      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo:
            typeof window !== 'undefined'
              ? `${window.location.origin}/auth/callback`
              : undefined,
          queryParams: {
            access_type: 'offline',
            prompt: 'select_account',
          },
        },
      })
      return { data, error }
    } catch {
      return { error: { message: 'Network error. Please try again.' } }
    }
  }

  const signOut = async () => {
    try {
      const { error } = await supabase.auth.signOut()
      if (!error) {
        setUser(null)
        profileOperations.clear()
      }
      return { error }
    } catch {
      return { error: { message: 'Network error. Please try again.' } }
    }
  }

  const updateProfile = async (updates: Partial<UserProfile>) => {
    if (!user) return { error: { message: 'No user logged in' } }
    try {
      const { data, error } = await supabase
        .from('users')
        .update(updates)
        .eq('id', user.id)
        .select()
        .single()
      if (!error) setUserProfile(data)
      return { data, error }
    } catch {
      return { error: { message: 'Network error. Please try again.' } }
    }
  }

  const value: AuthContextType = {
    user,
    userProfile,
    loading,
    profileLoading,
    signIn,
    signInWithGoogle,
    signOut,
    updateProfile,
    isAuthenticated: !!user,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}
